import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideBrowserGlobalErrorListeners } from '@angular/core';
import { RouteReuseStrategy, provideRouter, withPreloading } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule } from '@ionic/angular';
import { provideAuth0 } from '@auth0/auth0-angular';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { CustomReuseStrategy } from './core/custom-reuse-strategy';
import { SelectivePreloadStrategy } from './core/selective-preload.strategy';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { correlationInterceptor } from './core/interceptors/correlation.interceptor';
import { GlobalErrorHandler } from './core/errors/global-error-handler';
import { SessionStorageCache } from './core/auth/session-storage-cache';
import { MOCK_INTERCEPTORS, MOCK_PROVIDERS } from './core/mock/mock-providers';

export const appConfig: ApplicationConfig = {
  providers: [
    // Router: SelectivePreloadStrategy background-preloads lazy feature chunks except
    // those opting out with route data `{ preload: false }` (company: echarts, devices:
    // VNC/VPN, admin: platform-admin only) — see selective-preload.strategy.ts.
    provideRouter(routes, withPreloading(SelectivePreloadStrategy)),
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },

    // Unhandled errors and promise rejections reach GlobalErrorHandler, which reports them to the backend log.
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // The mock API answers last, so the auth interceptor also sees its errors (401 step-up, 403, 5xx) like the real ones.
    provideHttpClient(withXhr(), withInterceptors([correlationInterceptor, authInterceptor, ...MOCK_INTERCEPTORS])),

    // Auth0, or the fake sign-in of the mock mode (core/mock, empty in every other build).
    ...(MOCK_PROVIDERS.length ? MOCK_PROVIDERS : [provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      // Tab-scoped token cache instead of localStorage: the refresh token never reaches
      // disk and no second tab inherits the session, but a reload keeps you logged in.
      // The previous 'memory' default forced a full redirect login on every reload —
      // its documented recovery (iframe silent auth) needs the Auth0 cookie in a
      // third-party context, which Firefox and Safari block by default. See the class
      // doc in session-storage-cache.ts for the full reasoning.
      cache: new SessionStorageCache(),
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: {
        redirect_uri: window.location.origin + '/callback',
        audience: environment.auth0.audience,
        scope: 'openid profile email offline_access',
      }
    })]),

    // i18n
    provideTranslateService({ fallbackLang: 'de' }),
    ...provideTranslateHttpLoader({ prefix: 'assets/translations/', suffix: '.json' }),

    // @ionic/angular has no standalone provideIonicAngular() equivalent for this version;
    // importProvidersFrom is the established pattern for it across this portfolio's other
    // migrated apps (Demo-SPA, Developer-SPA, ScreenDeck). No { mode: ... } override was
    // ever configured here (verified: IonicModule.forRoot() took no arguments pre-migration).
    importProvidersFrom(BrowserModule, BrowserAnimationsModule, IonicModule.forRoot(), FormsModule, ReactiveFormsModule, DragDropModule),
  ],
};
